import classNames from "classnames";
import type {
  GetServerSideProps,
  InferGetServerSidePropsType,
  NextPage
} from "next";
import Head from "next/head";
import Link from "next/link";
import { v4 } from "uuid";
import Advertisement from "../../../components/Advertisement";
import ArticleTag from "../../../components/Article/ArticleTag";
import ArticleItem from "../../../components/ArticleItem";
import Container from "../../../components/Container";
import Footer from "../../../components/Footer";
import PrimaryGrid from "../../../components/Grid/PrimaryGrid";
import SecondaryGrid from "../../../components/Grid/SecondaryGrid";
import { Media } from "../../../components/Media";
import Money from "../../../components/Money";
import Navigation from "../../../components/Navigation";
import ProfileTable from "../../../components/pages/agent/ProfileTable";
import ProfileImage from "../../../components/ProfileImage";
import ReadMore from "../../../components/ReadMore";
import SectionHeader from "../../../components/SectionHeader";
import StickySidebar from "../../../components/StickySidebar";
import Table from "../../../components/Table/Table";
import TableBody from "../../../components/Table/TableBody";
import TableFooter from "../../../components/Table/TableFooter";
import TableHeaders from "../../../components/Table/TableHeaders";
import AdsIds from "../../../data/ads";
import { listByIds } from "../../../services/api/ads";
import {
  getAgency, getAgencyAgents,
  getAgent, getAgentNews,
  getAgentPlayers
} from "../../../services/api/agent";
import { API } from "../../../services/api/types";
import { AdsContext } from "../../../utils/AdsContext";
import checkImageWithFallback from '../../../utils/checkImageWithFallback';
import { DeviceTypeProvider } from "../../../utils/deviceTypeContext";
import { createServerSidePropsError } from "../../../utils/errorHandling/createServerSidePropsError";
import { logError } from "../../../utils/errorHandling/logError";
import { DeviceType, getDeviceType } from "../../../utils/getDeviceType";
import slugify from "../../../utils/slugify";
import { sortAlphabetically } from "../../../utils/sortAlphabetically";
import FavoriteToggle from "../../../components/FavoriteToggle";
import useCanonicalUrl from '../../../hooks/useCanonicalUrl';
import VideoPlayer from '../../../components/VideoPlayer';

type GetServerSidePropsParams = {
  slug: string;
  id: string;
};

export const getServerSideProps: GetServerSideProps<
  {
    agent?: API.Agent;
    news?: API.Article[];
    players?: API.AgentPlayer[];
    ads?: API.Ad[];
    deviceType?: DeviceType;
    agencyAgents?: API.AgencyAgent[]
    agency?: API.Agency
    agencyPlayers?: API.AgentPlayer[];
    agentImageSrc?: string;
  },
  GetServerSidePropsParams
> = async (context) => {
  try {
    const { slug, id } = context.params!;

    // id 60 = Agent okänd and has ~6500 players.
    const [agent, news, players, ads] = await Promise.all([
      getAgent(id),
      getAgentNews(id),
      id !== "60" ? getAgentPlayers(id) : (Promise.resolve({}) as FixMeLater),
      listByIds([
        AdsIds.panorama_1,
        AdsIds.panorama_2,
        AdsIds.mobile_1,
        AdsIds.mobile_2,
        AdsIds.tws_1,
        AdsIds.tws_2,
        AdsIds.desktop_vi,
        AdsIds.mobile_vi,
      ]),
      id !== "60" ? getAgencyAgents(id) : (Promise.resolve({}) as FixMeLater),
      id !== "60" ? getAgency(id) : (Promise.resolve({}) as FixMeLater)
    ]);

    if (!agent.data?.id) {
      context.res.statusCode = 404;
      
      return createServerSidePropsError(404)
    };

    const agencyAgents = agent.data?.agency !== null && Boolean(agent.data?.agency)
    ? await getAgencyAgents(id)
    : null;

    const agencyPlayers = agent.data?.agencyId
    ? await getAgentPlayers(agent.data.agencyId)
    : null;

    const agency = agent.data?.agencyId
    ? await getAgency(agent.data?.agencyId)
    : null;

    const correctSlug = slugify(agent.data.name);

    if (correctSlug !== slug)
      return {
        redirect: {
          destination: `/agenter/${correctSlug}/${id}`,
          permanent: true,
        },
      };

      const agentImageSrc = await checkImageWithFallback(
        agent.data.imageLarge,
        "/profile-fallback.jpg"
      );
  
      if (agentImageSrc === "/profile-fallback.jpg") {
        agent.data.imageSource = null;
      }

    return {
      props: {
        agent: agent.data,
        news: news.data,
        players: players.data || [],
        ads: ads.data,
        deviceType: getDeviceType(context),
        agencyAgents: agencyAgents?.data || [],
        agency: agency?.data || null,
        agencyPlayers: agencyPlayers?.data || [],
        agentImageSrc
      },
    };
  } catch (error: FixMeLater) {
    const errorId = v4();
    const isUserError =
      error.response?.status >= 400 && error.response?.status < 500;
    const response = createServerSidePropsError(
      isUserError ? 404 : 500,
      isUserError ? undefined : errorId
    );

    if (!isUserError) logError(error, errorId);

    context.res.statusCode = isUserError ? 404 : 500;

    return response;
  }
};

const AgentScreen: NextPage<
  InferGetServerSidePropsType<typeof getServerSideProps>
> = (props) => {
  const { agent, news, players, agencyAgents, agency, agencyPlayers, agentImageSrc } = props;

  const allPlayers = [...(players || []), ...(agencyPlayers || [])];

  const totalValue = allPlayers!
    .map((p) => (p.value < 0 ? 0 : p.value))
    .reduce((prev, curr) => prev + curr, 0);

  const canonicalUrl = useCanonicalUrl();

  return (
    <AdsContext.Provider value={props.ads!}>
      <DeviceTypeProvider value={props.deviceType!}>
        <Head>
          <title>{`${agent?.name} - Spelare & klubbar - Fotbolltransfers`}</title>
          <meta
            name="description"
            content={`${agent?.name} - Läs det senaste om fotbollsagenten ${agent?.name} och spelare hos oss på Fotbolltransfers. Vi är Sveriges största sajt om fotbollsövergångar.`}
          />
          <meta property="og:image" content={agentImageSrc} />
          <link rel="canonical" href={canonicalUrl} />
        </Head>

        <Container>
          <Navigation />
        </Container>

        <Container>
          <PrimaryGrid
            top={
              <>
                <Media greaterThanOrEqual="panorama">
                  <Advertisement id={AdsIds.panorama_1} />
                </Media>
                <Media lessThan="panorama">
                  <Advertisement id={AdsIds.mobile_1} />
                </Media>
              </>
            }
            right={
              <StickySidebar>
                <Advertisement id={AdsIds.tws_1} />
                <div className={classNames("mb-5")} />
                <Advertisement id={AdsIds.tws_2} />
              </StickySidebar>
            }
            main={
              <>
                <SecondaryGrid
                  left={
                    <>
                      <ProfileImage
                        person={agent!}
                        imgSrc={agentImageSrc!}
                        verified={[1, 2, 4].includes(agent?.licenseId || 0)}
                        type="agent"
                      />

                      <Media lessThan="md">
                        <>
                          <ProfileTable agent={agent!} />
                          <div className={classNames("mb-5")} />
                        </>
                      </Media>

                      {agencyAgents?.length ? <Media lessThan="md">
                      <SectionHeader label="Agenter" />
                              {agencyAgents
                                .map((agencyAgent) => (
                                  <div
                                    className={classNames("border-t")}
                                    key={agencyAgent.id}
                                  >
                                    <ArticleTag
                                      href={`/agenter/${slugify(
                                        agencyAgent.name
                                      )}/${agencyAgent.id}`}
                                      label={agencyAgent.name}
                                      imageSrc={`${process.env.NEXT_PUBLIC_ASSET_SERVER_URL}${agencyAgent.image}`}
                                      roundImage={true}
                                      id={agencyAgent.id}
                                      type="agent"
                                    />
                                  </div>
                                ))}
                                
                                <div className={classNames("mb-5")} />
                      </Media> : null}

                      {agency ? <Media lessThan="md">
                      <SectionHeader label="Agentur" />
                      
                                  <div
                                    className={classNames("border-t")}
                                    key={agency.id}
                                  >
                                    <ArticleTag
                                      href={`/agenter/${slugify(
                                        agency.name
                                      )}/${agency.id}`}
                                      label={agency.name}
                                      imageSrc={`${process.env.NEXT_PUBLIC_ASSET_SERVER_URL}${agency.image}`}
                                      roundImage={true}
                                      id={agency.id}
                                      type="agent"
                                    />
                                  </div>
                             
                                
                                <div className={classNames("mb-5")} />
                      </Media> : null}

                      <Media lessThan="md">
                            <SectionHeader label="Video" />
                            <div className='py-5 min-h-[310px]'>
                              <VideoPlayer />
                            </div>
                          </Media>

                      <SectionHeader label="Spelare" />
                      <Table>
                        <TableHeaders
                          headers={[
                            {
                              text: "Spelare / Klubb",
                              style: { width: "100%" },
                            },
                            "Marknadsvärde",
                          ]}
                        />
                        <TableBody>
                          {players!
                            .sort((a, b) => sortAlphabetically(a.name, b.name))
                            .sort((a, b) => b.value - a.value)
                            .map((player, index) => (
                              <tr key={index}>
                                <td>
                                  <div className="flex items-center gap-3">
                                    <FavoriteToggle
                                      favorite={{
                                        id: player.id,
                                        name: player.name,
                                        image: player.image,
                                      }}
                                      type="player"
                                    />

                                  <div>
                                    <span
                                      className={classNames([
                                        "font-bold",
                                        "block",
                                        "mb-0.5",
                                        "hover:underline",
                                      ])}
                                    >
                                      <Link
                                        href={`/spelare/${slugify(player.name)}/${
                                          player.id
                                        }`}
                                        passHref
                                      >
                                        <a>{player.name}</a>
                                      </Link>
                                    </span>
                                    <span
                                      className={classNames("hover:underline")}
                                    >
                                      {Boolean(player.showClub) ? player.clubsName && player.clubsId && (
                                        <Link
                                          href={`/klubbar/land/${slugify(
                                            player.clubsName
                                          )}/${player.clubsId}`}
                                          passHref
                                        >
                                          <a>{player.clubsName}</a>
                                        </Link>
                                      ) : "-"}
                                    </span>
                                  </div>
                                </div>
                                </td>
                                <td className={classNames("text-right")}>
                                  {player.value > 0 ? (
                                    <Money value={player.value} />
                                  ) : (
                                    "-"
                                  )}
                                </td>
                              </tr>
                            ))}

                            {agencyPlayers?.length ? <>
                              <span className='mt-2 mb-1 block ml-[1.25em] font-semibold'>Spelare från {agency?.name}:</span>
                              {agencyPlayers
                            .sort((a, b) => sortAlphabetically(a.name, b.name))
                            .sort((a, b) => b.value - a.value)
                            .map((player, index) => (
                              <tr key={index}>
                                <td>
                                  <span
                                    className={classNames([
                                      "font-bold",
                                      "block",
                                      "mb-0.5",
                                      "hover:underline",
                                    ])}
                                  >
                                    <Link
                                      href={`/spelare/${slugify(player.name)}/${
                                        player.id
                                      }`}
                                      passHref
                                    >
                                      <a>{player.name}</a>
                                    </Link>
                                  </span>
                                  <span
                                    className={classNames("hover:underline")}
                                  >
                                    {Boolean(player.showClub) ? player.clubsName && player.clubsId && (
                                      <Link
                                        href={`/klubbar/land/${slugify(
                                          player.clubsName
                                        )}/${player.clubsId}`}
                                        passHref
                                      >
                                        <a>{player.clubsName}</a>
                                      </Link>
                                    ) : "-"}
                                  </span>
                                </td>
                                <td className={classNames("text-right")}>
                                  {player.value > 0 ? (
                                    <Money value={player.value} />
                                  ) : (
                                    "-"
                                  )}
                                </td>
                              </tr>
                            ))}
                            </> : null}
                        </TableBody>
                        <TableFooter rowHeight="sm">
                          <tr className={classNames("border-t")}>
                            <td
                              className={classNames("text-right")}
                              colSpan={2}
                            >
                              <span className={classNames(["mr-1"])}>
                                Totalt:
                              </span>
                              <Money
                                value={totalValue}
                                style={["inline-block"]}
                                decimals={1}
                              />
                            </td>
                          </tr>
                        </TableFooter>
                      </Table>
                      <div className={classNames("mb-5")} />

                      <Media lessThan="md">
                        <>
                          <Advertisement id={AdsIds.mobile_vi} />
                          <div className={classNames("mb-5")} />
                          <SectionHeader label="Senaste nytt" />
                          {news!.slice(0, 5).map((article) => {
                            return (
                              <div
                                key={article.id}
                                className={classNames([
                                  "py-5",
                                  "px-5",
                                  "border-b",
                                ])}
                              >
                                <ArticleItem
                                  type={"small"}
                                  article={article}
                                  ratio="16:9"
                                />
                              </div>
                            );
                          })}

                          {news!.length > 0 ? (
                            <>
                              <ReadMore
                                label={`Alla nyheter om ${agent!.name}`}
                                href={`/agenter/${slugify(agent!.name)}/${
                                  agent!.id
                                }/nyheter`}
                              />
                              <div className={classNames("mb-5")} />
                            </>
                          ) : (
                            <p
                              className={classNames([
                                "py-5",
                                "border-b",
                                "p-4",
                                "font-semibold",
                              ])}
                            >
                              Inga nyheter att visa
                            </p>
                          )}
                        </>
                      </Media>
                    </>
                  }
                  right={
                    <>
                      <SectionHeader label="Video" />
                      <div className='py-5 min-h-[291px]'>
                        <VideoPlayer />
                      </div>

                      <ProfileTable agent={agent!} />
                      <div className={classNames("mb-5")} />

                      {agencyAgents?.length ? <>
                        <SectionHeader label="Agenter" />
                              {agencyAgents
                                .map((agencyAgent) => (
                                  <div
                                    className={classNames("border-t")}
                                    key={agencyAgent.id}
                                  >
                                    <ArticleTag
                                      href={`/agenter/${slugify(
                                        agencyAgent.name
                                      )}/${agencyAgent.id}`}
                                      label={agencyAgent.name}
                                      imageSrc={`${process.env.NEXT_PUBLIC_ASSET_SERVER_URL}${agencyAgent.image}`}
                                      roundImage={true}
                                      id={agencyAgent.id}
                                      type="agent"
                                    />
                                  </div>
                                ))}

                      <div className={classNames("mb-5")} />
                      </> : null}

                      {agency  ? <>
                        <SectionHeader label="Agentur" />
                      
                      <div
                        className={classNames("border-t")}
                        key={agency.id}
                      >
                        <ArticleTag
                          href={`/agenter/${slugify(
                            agency.name
                          )}/${agency.id}`}
                          label={agency.name}
                          imageSrc={`${process.env.NEXT_PUBLIC_ASSET_SERVER_URL}${agency.image}`}
                          roundImage={true}
                          id={agency.id}
                          type="agent"
                        />
                      </div>
                 
                    
                    <div className={classNames("mb-5")} />
                      </> : null}

                      <SectionHeader label="Senaste nytt" />
                      {news!.slice(0, 5).map((article) => {
                        return (
                          <div
                            key={article.id}
                            className={classNames(["py-5", "border-b"])}
                          >
                            <ArticleItem type={"small"} article={article} />
                          </div>
                        );
                      })}

                      {news!.length > 0 ? (
                        <ReadMore
                          label={`Alla nyheter om ${agent!.name}`}
                          href={`/agenter/${slugify(agent!.name)}/${
                            agent!.id
                          }/nyheter`}
                        />
                      ) : (
                        <p
                          className={classNames([
                            "py-5",
                            "border-b",
                            "p-4",
                            "font-semibold",
                          ])}
                        >
                          Inga nyheter att visa
                        </p>
                      )}

                      <div className={classNames("mb-5")} />
                      <Advertisement id={AdsIds.desktop_vi} />
                    </>
                  }
                />
                <Media greaterThanOrEqual="panorama">
                  <Advertisement id={AdsIds.panorama_2} />
                </Media>
                <Media lessThan="panorama">
                  <Advertisement id={AdsIds.mobile_2} />
                </Media>
              </>
            }
          />
          <div className={classNames("mb-5")} />
          <Footer />
        </Container>
      </DeviceTypeProvider>
    </AdsContext.Provider>
  );
};

export default AgentScreen;
